import React, { useState, useEffect, useMemo } from "react";
import { ApplicationRoles } from "../../Apis/Organization-User";
import {
  TextField,
  Grid,
  Alert,
  Autocomplete,
  MenuItem,
  Switch,
  FormControlLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { fetchUserTypes } from "../../Apis/UserTypes";
import CommonFormLayout from "../EmsOptimized/Form/CommonFormLayout";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import axios from "axios";
import { MAIN_URL } from "../../Configurations/Urls";
import { fetchGeneralCountries } from "../../Apis/OrganizationLocation";
import SelectWithImage from "../../Components/Inputs/SelectWithImage";
import {
  cannotDeactivateOrganizationUser,
  extractToggleStatusError,
  getCannotDeactivateMessage,
  isCurrentOrganizationUser,
} from "../../Utils/organizationUserToggle";
import { resolveOrganizationUserId } from "../../Utils/organizationSession";

function readUserIsActive(user) {
  const value = user?.is_active;
  return value === true || value === 1 || value === "1";
}

const isSharedResourceUserType = (type) => {
  const name = String(type?.user_type_name || "").trim().toLowerCase();
  return name === "shared resource" || name === "shared resources";
};

function UsersEdit({ mode = "edit" }) {
  const isViewMode = mode === "view";
  const [btnLoading, setBtnLoading] = useState(false);
  const [Roles, setRoles] = useState([]);
  const { userData } = useAuthStore();
  const [userTypes, setUserTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [countryCode, setCountryCode] = useState(null);
  const [loading, setLoading] = useState();
  const [formErrors, setFormErrors] = useState({});
  const [otherApplicationRoles, setOtherApplicationRoles] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [statusToggling, setStatusToggling] = useState(false);
  const [statusConfirm, setStatusConfirm] = useState(null);
  const [userCounts, setUserCounts] = useState({
    total: 0,
    total_users_count: 0,
    active_users_count: 0,
  });
  const [userDetails, setUserDetails] = useState({
    full_name: "",
    phone_number: "",
    email: "",
    organization_user_type_id: "",
    phone_code: "",
    organization_user_role_id: "",
    organization_user_role_ids: [],
    application_user_role_id: "",
  });

  const org = userData?.organization;
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!org?.organization_id) {
      return;
    }
    ApplicationRoles(org.organization_id)
      .then((data) => {
        setRoles(data?.userroles);
      })
      .catch((err) => {
        setFormErrors(err.message);
      });
  }, [org?.organization_id]);

  useEffect(() => {
    // Custom organization roles already have their organization role ID.
    if (!Roles?.length || userDetails.organization_user_role_id) {
      return;
    }

    if (!userDetails.application_user_role_id) {
      return;
    }

    const match = Roles.find(
      (role) =>
        String(role.application_user_role_id) ===
        String(userDetails.application_user_role_id)
    );

    if (match) {
      setUserDetails((prev) => ({
        ...prev,
        organization_user_role_id: String(match.organization_user_role_id),
      }));
    }
  }, [
    Roles,
    userDetails.application_user_role_id,
    userDetails.organization_user_role_id,
  ]);

  console.log("system roles", Roles);

  useEffect(() => {
    fetchGeneralCountries()
      .then((data) => {
        const formatted = data.countries.map((item) => ({
          name: item.country_name,
          code: item.country_code,
          phone_code: item.country_phone_code,
        }));
        setCountries(formatted);
        setCountryCode(formatted.find((item) => item.code === "IN"));
      })
      .catch((err) => setFormErrors(err.message));
  }, []);

  useEffect(() => {
    fetchUserTypes(org?.organization_id)
      .then((data) => {
        setUserTypes(data?.userTypes?.data);
      })
      .catch((err) => {
        setFormErrors(err.message);
      });
  }, []);

  useEffect(() => {
    if (!org?.organization_id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(
          `${MAIN_URL}/api/organizations/${org.organization_id}/user/filtered`,
          {
            filters: [],
            page: 1,
            pageSize: 1,
            sort: { field: "application_user_full_name", direction: "asc" },
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (cancelled) return;
        const pag = res.data?.pagination ?? {};
        const total = Number(pag.total ?? 0);
        setUserCounts({
          total,
          total_users_count: Number(pag.total_users_count ?? total),
          active_users_count: Number(pag.active_users_count ?? 0),
        });
      } catch (_) {
        /* backend still validates toggle */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id]);

  // get user by id
  useEffect(() => {
    let getdataById = async () => {
      const response = await axios.get(
        `${MAIN_URL}/api/organizations/${org.organization_id}/user/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log("reponse sos id is ", response);
      const a = response.data.user;
      const currentAppRole =
        a?.role_assignment?.application_user_role?.application_user_role_id;
      const currentOrgRole = a?.role_assignment?.organization_user_role_id;
      const assignedRoleIds = (
        a?.role_assignments ||
        a?.RoleAssignments ||
        []
      )
        .map((item) => String(item?.organization_user_role_id || "").trim())
        .filter(Boolean);
      if (currentOrgRole && !assignedRoleIds.includes(String(currentOrgRole))) {
        assignedRoleIds.unshift(String(currentOrgRole));
      }

      setOtherApplicationRoles(a?.other_application_roles || []);
      setIsActive(readUserIsActive(a));
      setUserDetails({
        email: a?.applicationuser?.[0]?.email,
        phone_number: a?.applicationuser?.[0]?.phone_number,
        full_name: a?.applicationuser?.[0]?.full_name,
        organization_user_type_id: a?.organization_user_type_id,
        phone_code: a?.applicationuser[0]?.country_phone_code,
        organization_user_role_id: currentOrgRole ? String(currentOrgRole) : assignedRoleIds[0] || "",
        organization_user_role_ids: assignedRoleIds,
        application_user_role_id: currentAppRole ? String(currentAppRole) : "",
        application_user_id:
          a?.application_user_id ??
          a?.applicationuser?.[0]?.application_user_id ??
          "",
        organization_user_id: a?.organization_user_id ?? id,
      });
      setLoading(false);
    };
    if (id) {
      setLoading(true);
      getdataById();
    }
  }, [id]);

  console.log("formdata", userDetails);

  const handleDetailChange = (e) => {
    const { name, value } = e.target;

    if (name === "organization_user_role_ids") {
      const selected = Array.isArray(value) ? value : [];
      const first = selected[0];
      setUserDetails((prev) => ({
        ...prev,
        organization_user_role_ids: selected.map((role) =>
          String(role.organization_user_role_id)
        ),
        organization_user_role_id: first
          ? String(first.organization_user_role_id)
          : "",
        application_user_role_id:
          first?.application_user_role_id != null
            ? String(first.application_user_role_id)
            : "",
      }));
      setFormErrors((prev) => ({ ...prev, organization_user_role_id: "" }));
      return;
    }

    if (name === "organization_user_role_id") {
      const selectedRole = Roles.find(
        (role) => String(role.organization_user_role_id) === String(value)
      );
      setUserDetails((prev) => ({
        ...prev,
        organization_user_role_id: value,
        organization_user_role_ids: value ? [String(value)] : [],
        application_user_role_id:
          selectedRole?.application_user_role_id != null
            ? String(selectedRole.application_user_role_id)
            : "",
      }));
      setFormErrors((prev) => ({ ...prev, organization_user_role_id: "" }));
      return;
    }

    setUserDetails((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  useEffect(() => {
    if (countryCode?.phone_code) {
      setUserDetails((prev) => ({
        ...prev,
        phone_code: countryCode.phone_code,
      }));
    }
  }, [countryCode]);

  const isValidEmail = (email) => {
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return emailRegex.test(email.trim());
  };

  const validateForm = () => {
    const errors = {};

    // if (!userDetails.email) errors.email = "User's email is required.";

    // const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // if (!emailRegex.test(userDetails.email)) {
    //   errors.email = "Please enter a valid email address.";
    // }

    if (userDetails.email && !isValidEmail(userDetails.email)) {
      errors.email = "Enter a valid  email address.";
    }

    if (!userDetails.email) errors.email = "Email is required.";

    if (!userDetails.full_name) errors.full_name = "User's Name is required.";

    if (!userDetails.phone_number) errors.phone_number = "Phone is required.";

    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const toggleUserRow = {
    organization_user_id: id,
    is_active: isActive ? 1 : 0,
    __is_active_for_toggle: isActive,
  };
  const cannotDeactivate = cannotDeactivateOrganizationUser(
    toggleUserRow,
    userCounts
  );
  const isEditingSelf = isCurrentOrganizationUser(
    {
      organization_user_id: id,
      application_user_id: userDetails.application_user_id,
    },
    userData
  ) || String(id) === String(resolveOrganizationUserId(userData) || "");
  const statusDisabled =
    isViewMode ||
    statusToggling ||
    isEditingSelf ||
    (isActive && cannotDeactivate);
  const deactivateBlockedReason = isEditingSelf
    ? "You cannot deactivate your own account."
    : cannotDeactivate
      ? getCannotDeactivateMessage(userCounts)
      : "";

  const userTypeOptions = useMemo(() => {
    const types = Array.isArray(userTypes) ? userTypes : [];
    const currentType = types.find(
      (type) =>
        String(type?.organization_user_type_id) ===
        String(userDetails.organization_user_type_id)
    );

    if (isSharedResourceUserType(currentType)) {
      return currentType ? [currentType] : [];
    }

    return types.filter((type) => !isSharedResourceUserType(type));
  }, [userTypes, userDetails.organization_user_type_id]);

  const isEditingSharedResourceUser = useMemo(() => {
    const currentType = userTypes?.find(
      (type) =>
        String(type?.organization_user_type_id) ===
        String(userDetails.organization_user_type_id)
    );
    return isSharedResourceUserType(currentType);
  }, [userTypes, userDetails.organization_user_type_id]);

  const handleStatusToggleRequest = (event) => {
    const nextActive = event.target.checked;
    if (isEditingSelf && !nextActive) {
      toast.error("You cannot deactivate your own account.");
      return;
    }
    if (
      !nextActive &&
      cannotDeactivateOrganizationUser(
        { ...toggleUserRow, __is_active_for_toggle: true },
        userCounts
      )
    ) {
      toast.error(getCannotDeactivateMessage(userCounts));
      return;
    }
    setStatusConfirm({ nextActive });
  };

  const confirmStatusChange = async () => {
    if (!statusConfirm) return;
    const nextActive = statusConfirm.nextActive;
    setStatusConfirm(null);
    setStatusToggling(true);
    try {
      await axios.put(
        `${MAIN_URL}/api/organizations/${org.organization_id}/user/${id}/toggle`,
        { is_active: nextActive },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setIsActive(nextActive);
      setUserCounts((prev) => ({
        ...prev,
        active_users_count: nextActive
          ? Number(prev.active_users_count ?? 0) + 1
          : Math.max(Number(prev.active_users_count ?? 0) - 1, 0),
      }));
      toast.success(
        `User status updated to ${nextActive ? "Active" : "Inactive"}`
      );
    } catch (err) {
      toast.error(extractToggleStatusError(err));
    } finally {
      setStatusToggling(false);
    }
  };

  const handleFinalSubmit = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);

    try {
      // Prepare payload with only allowed fields for update
      const payload = {
        full_name: userDetails.full_name,
        email: userDetails.email,
        phone_number: userDetails.phone_number,
        phone_code: userDetails.phone_code,
        organization_user_type_id: userDetails.organization_user_type_id,
        email:userDetails?.email,
        organization_user_role_ids: (userDetails?.organization_user_role_ids || [])
          .map((id) => Number(id))
          .filter((id) => id > 0),
        organization_user_role_id: userDetails?.organization_user_role_id
          ? Number(userDetails.organization_user_role_id)
          : null,
        application_user_role_id: userDetails?.application_user_role_id
          ? Number(userDetails.application_user_role_id)
          : null,
      };

      await axios.put(
        `${MAIN_URL}/api/organizations/${org.organization_id}/user/${id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success("User Updated!");
      setFormErrors({});
      navigate(-1);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 422) {
        const validationErrors =
          err.response.data.message || err.response.data.errors || {};
        setFormErrors(validationErrors);
        const errorMessages = Object.values(validationErrors).flat().join(" ");
        toast.error(errorMessages || "Validation failed.");
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <CommonFormLayout
      mode={mode}
      loading={loading}
      btnLoading={btnLoading}
      headerProps={{
        addMessage: "Add User",
        updateMessage: "Edit User",
        homeLink: "/users",
        homeText: "Users",
      }}
      onSubmit={handleFinalSubmit}
    >
      {otherApplicationRoles.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This user has role(s) in other applications:{" "}
          {otherApplicationRoles.map((role) => role.user_role_name).join(", ")}.
          Select a role below to assign access for this application.
        </Alert>
      )}
      <Grid container spacing={2}>
          <TextField
            value={userDetails.full_name}
            error={!!formErrors.full_name}
            helperText={formErrors.full_name}
            fullWidth
            label="Full Name"
            name="full_name"
            onChange={handleDetailChange}
            disabled={isViewMode}
            required
          />

          <TextField
            value={userDetails.email}
            error={!!formErrors.email}
            helperText={formErrors.email}
            fullWidth
            label="Email"
            name="email"
            type="text"
            onChange={handleDetailChange}
            disabled={isViewMode}
            required
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              width: "100%",
            }}
          >
            <div
              style={{
                width: "25%",
              }}
            >
              <div
                style={{
                  border: "2px solid rgba(0, 0, 0, 0.23)",
                  borderRadius: "6px",
                  // padding: "1px 8px",
                }}
              >
                <SelectWithImage
                  value={countryCode}
                  error={false}
                  options={countries}
                  optionkey={"phone_code"}
                  onChange={(e, val) => setCountryCode(val)}
                  width="100%"
                  minWidth="20px"
                  disabled={isViewMode}
                  searchKeys={["name", "phone_code"]}
                />
              </div>
            </div>

            <TextField
              fullWidth
              label="Phone Number"
              name="phone_number"
              value={userDetails.phone_number}
              error={!!formErrors.phone_number}
              helperText={formErrors.phone_number}
              onChange={(e) => {
                const onlyDigits = e.target.value.replace(/\D/g, ""); // Remove non-digits
                if (onlyDigits?.length <= 20) {
                  handleDetailChange({
                    target: { name: "phone_number", value: onlyDigits },
                  });
                }
              }}
              disabled={isViewMode}
              required
              inputProps={{
                maxLength: 20,
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
            />
          </div>

          <Autocomplete
            multiple
            fullWidth
            options={Roles || []}
            getOptionLabel={(option) =>
              option.user_role_name || option.role_name || ""
            }
            isOptionEqualToValue={(option, value) =>
              String(option?.organization_user_role_id) ===
              String(value?.organization_user_role_id)
            }
            value={(Roles || []).filter((role) =>
              (userDetails.organization_user_role_ids || []).includes(
                String(role.organization_user_role_id)
              )
            )}
            onChange={(event, selected) =>
              handleDetailChange({
                target: {
                  name: "organization_user_role_ids",
                  value: selected,
                },
              })
            }
            disabled={isViewMode || !Roles?.length}
            renderInput={(params) => (
              <TextField
                {...params}
                label="User Roles"
                name="organization_user_role_id"
                error={!!formErrors.organization_user_role_id}
                helperText={
                  formErrors.organization_user_role_id ||
                  "A person can have Staff and Trainer roles at the same time."
                }
              />
            )}
          />

          <TextField
            select
            fullWidth
            label="User Type"
            name="organization_user_type_id"
            value={userDetails.organization_user_type_id}
            onChange={handleDetailChange}
            error={!!formErrors.organization_user_type_id}
            helperText={formErrors.organization_user_type_id}
            disabled={isViewMode || isEditingSharedResourceUser}
            required
          >
            {userTypeOptions?.map((option) => (
              <MenuItem
                key={option?.organization_user_type_id}
                value={option?.organization_user_type_id}
              >
                {option?.user_type_name}
              </MenuItem>
            ))}
          </TextField>

          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mt: 1,
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: isActive
                  ? "rgba(8, 145, 178, 0.06)"
                  : "rgba(0,0,0,0.04)",
              }}
            >
              <Box>
                <div style={{ fontWeight: 700, fontSize: 14 }}>User status</div>
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>
                  {isEditingSelf
                    ? "You cannot deactivate your own account."
                    : isActive
                      ? "This user can sign in."
                      : "This user cannot sign in."}
                </div>
              </Box>
              <Tooltip
                title={
                  statusDisabled && (isEditingSelf || cannotDeactivate)
                    ? deactivateBlockedReason
                    : isActive
                      ? "Deactivate user"
                      : "Activate user"
                }
              >
                <span>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isActive}
                        onChange={handleStatusToggleRequest}
                        disabled={statusDisabled}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#0891B2",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            {
                              backgroundColor: "#0891B2",
                            },
                        }}
                      />
                    }
                    label={isActive ? "Active" : "Inactive"}
                  />
                </span>
              </Tooltip>
              {statusToggling && <CircularProgress size={20} />}
            </Box>
          </Grid>
        </Grid>

      <Dialog open={!!statusConfirm} onClose={() => setStatusConfirm(null)}>
        <DialogTitle>Confirm status change</DialogTitle>
        <DialogContent>
          Are you sure you want to set this user to{" "}
          <strong>{statusConfirm?.nextActive ? "Active" : "Inactive"}</strong>?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusConfirm(null)} disabled={statusToggling}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmStatusChange}
            disabled={statusToggling}
            sx={{
              bgcolor: "#0891B2",
              "&:hover": { bgcolor: "#067a9a" },
            }}
          >
            {statusToggling ? "Updating..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </CommonFormLayout>
  );
}

export default UsersEdit;
